import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import useContextStore from '@/hooks/useContextStore';
import type { ContextNode } from '@/types/context/context-page';
import {
  areNodesEqual,
  createEdgesFromNodes,
  transformNodes,
} from '@/utils/context/context-graph';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import type { ReactNode, SyntheticEvent } from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import Documents from './Documents';
import NetworkGraph from './NetworkGraph';
import NodeOperator from './NodeOperator';

// Memoize the NetworkGraph component to preserve its state
const MemoizedNetworkGraph = memo(NetworkGraph);

function ContextVisualizer(): ReactNode {
  const {
    setStoreState: setContextStoreState,
    contextNodes,
    selectedNodes,
  } = useContextStore((store) => ({
    setStoreState: store.setStoreState,
    contextNodes: store.contextNodes ?? [],
    selectedNodes: store.selectedNodes,
  }));
  const { setAlchemystStoreState, snackbarEntries } = useAlchemystStoreForAi(
    (store) => ({
      setAlchemystStoreState: store.setStoreState,
      snackbarEntries: store.snackbarEntries,
    })
  );
  const [tabValue, setTabValue] = useState(0);
  const prevNodesRef = useRef<ContextNode[]>([]);

  // Create graph data once and keep it consistent
  const graphData = useMemo(
    () => ({
      nodes: contextNodes,
      edges: createEdgesFromNodes(contextNodes),
    }),
    [contextNodes]
  );

  const getContextProcessedNodes = async () => {
    const contextNodesFetchResponse = await fetchWithRewrites(
      '/api/v1/context/view'
    );

    if (!contextNodesFetchResponse.ok) {
      throw new Error("Couldn't fetch contexts.");
    }
    const contextNodesFetchResponseJson =
      await contextNodesFetchResponse.json();
    console.log(
      'contextNodesFetchResponseJson = ',
      contextNodesFetchResponseJson
    );
    const fetchedNodes = contextNodesFetchResponseJson.context;
    // const nodes = transformNodes(sampleNodes);
    const nodes = transformNodes(fetchedNodes);
    console.log(nodes);

    // Deep compare with previous state before updating
    if (!areNodesEqual(prevNodesRef.current, nodes)) {
      setContextStoreState({
        contextNodes: nodes,
      });
      prevNodesRef.current = nodes;
    }
  };

  useEffect(() => {
    getContextProcessedNodes().catch((e) => {
      setAlchemystStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            message: "Couldn't visualize the contexts. Sorry :(",
            severity: 'error',
            type: 'alert',
            notificationId: crypto.randomUUID(),
          },
        ],
      });
    });
  }, []);

  const handleTabChange = (event: SyntheticEvent, tabIdx: number) => {
    setTabValue(tabIdx);
  };

  // Always render the NetworkGraph but hide it when not on the Dissect tab
  // This preserves its state between tab switches
  return (
    <>
      <Box sx={{ width: '100%', padding: '1rem 0' }}>
        <Tabs
          aria-label="context-visualizer-tabs"
          onChange={handleTabChange}
          role="navigation"
          value={tabValue}
        >
          <Tab label="Dissect" />
          <Tab label="Operate" />
          <Tab label="Documents" />
        </Tabs>
      </Box>

      {/* Always render the graph but conditionally show/hide it */}
      <div style={{ display: tabValue === 0 ? 'block' : 'none' }}>
        <Stack sx={{ my: 2 }} spacing={2}>
          <Typography variant="h5">Dissect</Typography>
          <Typography>
            Surf and search through your document contexts here, split across{' '}
            <b>{contextNodes.length}</b> piece of information.
          </Typography>
          <MemoizedNetworkGraph data={graphData} />
        </Stack>
      </div>

      {tabValue === 1 && (
        <Stack sx={{ my: 2 }} spacing={2}>
          <Typography variant="h5">Operate</Typography>
          <Typography>Edit/Delete your context here.</Typography>
          <NodeOperator nodes={selectedNodes} />
        </Stack>
      )}
      {tabValue === 2 && <Documents />}
    </>
  );
}

export default ContextVisualizer;
